import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  Newspaper, 
  Calendar, 
  Clock, 
  User, 
  ArrowLeft, 
  Share2, 
  Printer, 
  Sparkles, 
  Loader2, 
  Globe, 
  ChevronRight,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { API_BASE_URL } from "@/config";
import { RichBulletinRenderer } from "./dashboard/boletines/components/RichBulletinRenderer";

interface Bulletin {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  image_url?: string;
  category: string;
  status: "draft" | "published";
  read_time: string;
  published_at?: string;
  created_at: string;
  author_name: string;
}

const DEFAULT_COVER = "https://images.unsplash.com/photo-1450133064473-71024230f91b?q=80&w=1200&auto=format&fit=crop";

export default function PublicBulletinPage() {
  const { slug } = useParams<{ slug: string }>();
  const [bulletin, setBulletin] = useState<Bulletin | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setError(null);
    
    const base = (API_BASE_URL || import.meta.env.VITE_API_URL || (window.location.origin.includes("localhost") ? "http://localhost:8001" : window.location.origin)).replace(/\/$/, "");
    const endpoint = `${base}/api/v1/bulletins/${encodeURIComponent(slug)}`;
    
    // Get stored auth token if user is logged in
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    
    const sessionStr = localStorage.getItem("sb-legal-ai-auth-token") || localStorage.getItem("supabase.auth.token");
    if (sessionStr) {
      try {
        const parsed = JSON.parse(sessionStr);
        const token = parsed?.access_token || parsed?.currentSession?.access_token;
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
      } catch (e) {
        // ignore invalid JSON token
      }
    }

    fetch(endpoint, { headers })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Boletín no encontrado o no disponible.");
        }
        return res.json();
      })
      .then((data) => {
        if (data.bulletin) {
          setBulletin(data.bulletin);
        } else {
          setError("El boletín especificado no existe.");
        }
      })
      .catch((err) => {
        console.error("Error loading public bulletin:", err);
        setError("No se pudo cargar el boletín. Verifica tu conexión o intenta más tarde.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [slug]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Enlace público del boletín copiado al portapapeles");
  };

  const getCategoryBadgeClass = (category: string) => {
    switch (category) {
      case "Actualizaciones":
        return "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400";
      case "Nuevas Funciones":
        return "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400";
      case "Tutoriales":
        return "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400";
      case "Avisos":
        return "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400";
      default:
        return "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-400";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] dark:bg-slate-950 flex flex-col items-center justify-center p-6">
        <Loader2 className="w-10 h-10 text-pink-500 animate-spin mb-4" />
        <p className="text-slate-500 font-medium">Cargando publicación...</p>
      </div>
    );
  }

  if (error || !bulletin) {
    return (
      <div className="min-h-screen bg-[#f8fafc] dark:bg-slate-950 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 rounded-2xl bg-rose-50 dark:bg-rose-950/20 text-rose-500 flex items-center justify-center mb-4">
          <Newspaper className="w-8 h-8" />
        </div>
        <h2 className="text-2xl font-black text-slate-900 dark:text-white mb-2">
          Publicación no encontrada
        </h2>
        <p className="text-slate-500 max-w-md mb-6 text-sm">
          {error || "El boletín solicitado no existe o ha sido retirado."}
        </p>
        <Link to="/">
          <Button className="bg-pink-600 hover:bg-pink-700 text-white font-bold rounded-xl px-6 py-5">
            Volver a Mi Asistente Legal IA
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col selection:bg-pink-500 selection:text-white">
      {/* Top Navbar */}
      <header className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40 border-b border-slate-200/80 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-md shadow-pink-200 dark:shadow-none group-hover:scale-105 transition-transform">
              <Newspaper className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="font-extrabold text-slate-900 dark:text-white text-base tracking-tight leading-none">
                Mi Asistente Legal IA
              </span>
              <span className="text-[10px] text-pink-600 dark:text-pink-400 font-bold uppercase tracking-wider mt-0.5">
                Boletines y Novedades
              </span>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShare}
              className="rounded-xl text-slate-600 dark:text-slate-300 font-bold text-xs flex items-center gap-1.5"
            >
              <Share2 className="w-4 h-4 text-pink-500" />
              Compartir
            </Button>
            <Link to="/login">
              <Button className="bg-gradient-to-r from-pink-600 to-rose-600 text-white font-bold text-xs rounded-xl px-5 py-4 shadow-md hover:from-pink-700 hover:to-rose-700">
                Acceder a la Plataforma
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content Container */}
      <main className="flex-1 max-w-4xl w-full mx-auto p-4 sm:p-8 space-y-8 my-4">
        {/* Cover Header */}
        <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-800 bg-slate-900 group">
          <div className="relative h-72 sm:h-96 w-full">
            <img 
              src={bulletin.image_url || DEFAULT_COVER} 
              alt={bulletin.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-black/10" />

            <div className="absolute bottom-8 left-8 right-8 text-white space-y-4">
              <div className="flex items-center gap-3">
                <Badge className={cn("border-none px-3.5 py-1 font-extrabold uppercase text-xs tracking-wider shadow-md", getCategoryBadgeClass(bulletin.category))}>
                  {bulletin.category}
                </Badge>
                <span className="text-xs text-slate-300 font-semibold bg-white/10 px-3.5 py-1 rounded-full backdrop-blur-md border border-white/10">
                  {bulletin.read_time} de lectura
                </span>
              </div>
              <h1 className="text-2xl sm:text-4xl font-black leading-tight tracking-tight drop-shadow-md">
                {bulletin.title}
              </h1>
            </div>
          </div>
        </div>

        {/* Metadata Strip */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 sm:p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-wrap items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 text-white flex items-center justify-center font-bold text-sm shadow-sm">
              {bulletin.author_name.charAt(0).toUpperCase()}
            </div>
            <div>
              <span className="font-bold text-slate-900 dark:text-white block text-sm">
                {bulletin.author_name}
              </span>
              <span className="text-[11px] text-slate-400">Publicado por el Equipo Oficial</span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 font-medium">
            <Calendar className="w-4 h-4 text-pink-500" />
            <span>
              {bulletin.published_at 
                ? new Date(bulletin.published_at).toLocaleDateString("es-ES", {
                    day: "numeric",
                    month: "long",
                    year: "numeric"
                  })
                : new Date(bulletin.created_at).toLocaleDateString("es-ES")}
            </span>
          </div>
        </div>

        {/* Article Body Container */}
        <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 sm:p-12 border border-slate-200/80 dark:border-slate-800 shadow-sm">
          {bulletin.excerpt && (
            <p className="text-slate-600 dark:text-slate-300 text-lg font-medium leading-relaxed italic border-l-4 border-pink-500 pl-5 py-2 mb-8 bg-pink-50/50 dark:bg-pink-950/20 rounded-r-2xl">
              {bulletin.excerpt}
            </p>
          )}

          <RichBulletinRenderer content={bulletin.content} />
        </div>

        {/* Bottom Banner */}
        <div className="bg-gradient-to-r from-pink-600 via-rose-600 to-slate-900 rounded-3xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <h3 className="text-2xl font-black">
              ¿Quieres acceder a todas las herramientas legales con Inteligencia Artificial?
            </h3>
            <p className="text-pink-100 text-sm max-w-xl">
              Calculadoras automáticas, búsqueda de jurisprudencia, seguimiento judicial y generación de documentos en un solo lugar.
            </p>
          </div>
          <Link to="/login" className="shrink-0">
            <Button className="bg-white text-pink-600 hover:bg-slate-100 font-extrabold text-sm rounded-2xl px-8 py-6 shadow-lg hover:scale-102 transition-transform">
              Ingresar a la Plataforma <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 py-8 text-center text-xs text-slate-400">
        <p>© 2026 Mi Asistente Legal IA. Todos los derechos reservados.</p>
      </footer>
    </div>
  );
}
